import { FieldPathByValue, FieldValues, UseFormReturn } from "react-hook-form";
import useQueryString from "hooks/useQueryString";
import { Maybe, Schema as YupSchema } from "yup";
import { FloatingField } from "design-system";
import { PathId } from "features/Path/models";
import { TemplatePrefixId } from "features/Resources/SharedTemplate/models";
import { useTaskTemplateListQuery } from "features/Resources/TaskTemplate/hooks";
import { fromTemplatePrefixId } from "features/Resources/SharedTemplate/helpers";
import { Text16 } from "components/Text";
import { useEffect } from "react";
import { TriggerSchema } from "./schema";

interface TaskCompletionProps<TFieldName, TSchema extends FieldValues> {
  fieldName: TFieldName;
  pathIds: PathId[];
  form: UseFormReturn<TSchema>;
  currentTemplatePrefixId?: TemplatePrefixId;
}

export default function TaskCompletion<
  TSchema extends YupSchema,
  TFieldName extends FieldPathByValue<TSchema, Maybe<TriggerSchema>> = FieldPathByValue<TSchema, Maybe<TriggerSchema>>,
>({ pathIds, form, fieldName, currentTemplatePrefixId = "" }: TaskCompletionProps<TFieldName, TSchema>) {
  const name = `${fieldName}.data.dependencyId`;
  const {
    control,
    formState: { errors },
  } = form;
  const { queryString } = useQueryString({
    filters: [{ fieldName: "path_attachments.path_id", value: pathIds, operator: "in" }],
  });
  const { templateType, id } = fromTemplatePrefixId(currentTemplatePrefixId);
  const resolvedId = templateType === "task_template" ? id : "";
  const { data } = useTaskTemplateListQuery(queryString);
  const dependencyId = form.watch(`${fieldName}.data.dependencyId`);
  const dependencyType = form.getValues(`${fieldName}.data.dependencyType`);
  const event = form.getValues(`${fieldName}.data.event`);

  useEffect(() => {
    if (!dependencyId) return;
    if (dependencyType && event) return;

    form.setValue(`${fieldName}.data.dependencyType`, "TaskTemplate", {
      shouldDirty: true,
      shouldValidate: false,
    });
    form.setValue(`${fieldName}.data.event`, "task.completed", {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [dependencyId, dependencyType, event, fieldName, form, form.setValue]);

  return pathIds?.length ? (
    <FloatingField name={name} errors={errors[name]?.message as string | undefined}>
      <FloatingField.Label>Task</FloatingField.Label>
      <FloatingField.StaticSelect
        control={control}
        name={name}
        options={
          data?.filter((v) => v.id !== resolvedId).map((v) => ({ value: v.id, label: `${v.name}`, rawValue: v })) || []
        }
      />
    </FloatingField>
  ) : (
    <Text16>This template is not connected to a Path. Please select a path and return.</Text16>
  );
}
